import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { cn } from '@/lib/cn'

const inputVariants = cva(
  'flex w-full rounded-md border bg-system-background-base px-4 text-base  font-normal text-system-text-primary file:border-0 file:bg-transparent file:text-base file:font-medium placeholder:text-sm placeholder:font-normal placeholder:text-system-text-placeholder placeholder:text-system-text-secondary focus:border-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:border-system-background-grey disabled:bg-system-background-grey',
  {
    variants: {
      dimension: {
        default: 'h-10',
        sm: 'h-8',
      },
      variant: {
        default:
          'border-system-border-primary hover:border-system-primary-blue focus:border-system-selected-blue ',
        error: 'border-system-state-error focus:border-system-selected-red',
        success:
          'border-system-state-success focus:border-system-state-success',
      },
    },
    defaultVariants: {
      dimension: 'default',
      variant: 'default',
    },
  },
)

type Icon = {
  component: LucideIcon
  position: 'start' | 'end'
  size?: number
  className?: string
  onClick?: () => void
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof inputVariants> & {
    icons?: Icon[]
  }

const ICON_GAP = 4

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, icons, type, dimension, variant, ...props }, ref) => {
    const iconsStart =
      useMemo(
        () => icons?.filter(({ position }) => position === 'start'),
        [icons],
      ) ?? []

    const iconsEnd =
      useMemo(
        () => icons?.filter(({ position }) => position === 'end'),
        [icons],
      ) ?? []

    const paddingLeft = iconsStart.reduce(
      (acc, current) => acc + (current?.size ?? 16) + ICON_GAP,
      0,
    )

    const paddingRight = iconsEnd.reduce(
      (acc, current) => acc + (current?.size ?? 16) + ICON_GAP,
      0,
    )

    const variantColorMapper = {
      default: 'text-system-text-secondary',
      error: 'text-system-state-error',
      success: 'text-system-state-success',
    }

    const iconColor = variantColorMapper[variant ?? 'default']

    return (
      <div className="relative flex w-full items-center">
        <div className="absolute flex gap-1 px-4">
          {iconsStart.map(
            ({ component: Component, size = 16, className, onClick }) => {
              return (
                <Component
                  key={Component.displayName}
                  size={size}
                  role="img"
                  aria-label={Component.displayName}
                  className={cn(
                    iconColor,
                    onClick ? 'cursor-pointer' : '',
                    className,
                  )}
                  onClick={onClick}
                />
              )
            },
          )}
        </div>
        <input
          type={type}
          style={{
            paddingLeft: paddingLeft ? paddingLeft + 16 : 16,
            paddingRight: paddingRight ? paddingRight + 16 : 16,
          }}
          className={cn(inputVariants({ dimension, variant, className }))}
          ref={ref}
          {...props}
        />
        <div className="absolute right-0 flex gap-1 px-4">
          {iconsEnd.map(
            ({ component: Component, size = 16, className, onClick }) => {
              return (
                <Component
                  key={Component.displayName}
                  size={size}
                  role="img"
                  aria-label={Component.displayName}
                  className={cn(
                    iconColor,
                    onClick ? 'cursor-pointer' : '',
                    className,
                  )}
                  onClick={onClick}
                />
              )
            },
          )}
        </div>
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input, inputVariants }
